import React, { forwardRef, useEffect, useState } from "react";

import { formatIndianInputNumeric, normalizeNumericWithDecimal } from "../../../lib/utils";


//Keeps digits and decimal point example single decimal point, dropping anything past decimal
const sanitize = (text: string, decimals: number): string => {
  const [integer, fraction] = normalizeNumericWithDecimal(text).split(".");

  return fraction === undefined ? integer : `${integer}.${fraction.slice(0, decimals)}`;
};

// Text that is still half-typed ("", ".") has no value yet, so it reads as undefined
const toNumber = (text: string): number | undefined => {
  const numeric = Number(text);

  return text !== "" && Number.isFinite(numeric) ? numeric : undefined;
};

// The settled text for a value: exactly decimals digits after the point
export const toNumericText = (value: number | undefined, decimals: number): string => {
  if (value === undefined) return "";

  const [integer, fraction = ""] = String(value).split(".");

  return decimals ? `${integer}.${fraction.slice(0, decimals).padEnd(decimals, "0")}` : integer;
};

//Digit grouping shifts every character after the caret, so put the caret backbeside the digit the user was on
const restoreCaret = (input: HTMLInputElement, formatted: string, digitsBeforeCaret: number) => {
  queueMicrotask(() => {
    let seen = 0;
    let position = formatted.length;

    for (let i = 0; i < formatted.length; i++) {
      if (/[0-9.]/.test(formatted[i])) {
        if (seen === digitsBeforeCaret) {
          position = i;
          break;
        }
        seen += 1;
      }
    }

    input.setSelectionRange(position, position);
  });
};

interface NumericInputProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  decimals: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(({
  value,
  onChange,
  decimals,
  placeholder,
  disabled = false,
  className,
}, ref) => {
  const [draft, setDraft] = useState<string>(() => toNumericText(value, decimals));

  useEffect(() => {
    setDraft((current) =>
      toNumber(current) === value ? current : toNumericText(value, decimals)
    );
  }, [value, decimals]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const caret = input.selectionStart ?? input.value.length;

    const numeric = sanitize(input.value, decimals);
    const digitsBeforeCaret = sanitize(input.value.slice(0, caret), decimals).length;

    setDraft(numeric);
    onChange(toNumber(numeric));

    restoreCaret(input, formatIndianInputNumeric(numeric), digitsBeforeCaret);
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={formatIndianInputNumeric(draft)}
      placeholder={placeholder}
      disabled={disabled}
      onChange={handleChange}
      onBlur={() => setDraft(toNumericText(toNumber(draft), decimals))}
      className={className}
    />
  );
});

NumericInput.displayName = "NumericInput";
